/**
 * Card — surface container primitive (design-revamp / handoff-2026-06).
 *
 * Production translation of the prototype `Card`
 * (`docs/GardEn/handoff-2026-06/garden/ui.jsx`). The prototype toggles a
 * hover transform/shadow with React state; here `interactive` does it with
 * `hover:` utilities (CSS-driven, no state). Background, border, radius, and
 * the two shadow steps are all `--g-*` tokens.
 *
 * Elevation:
 *   default       → `--g-shadow-sm`
 *   elevated      → `--g-shadow-md`
 *   interactive   → lifts on hover (`-translate-y-0.5` + `--g-shadow-md`)
 *
 * When `onClick` is provided the card becomes a focusable, keyboard-
 * activatable `role="button"` (Enter/Space), so an interactive card is
 * reachable without a mouse.
 */
import type {
  KeyboardEvent as ReactKeyboardEvent,
  ReactElement,
  ReactNode,
} from "react";

export interface CardProps {
  /** Render the resting state at the medium shadow step. */
  elevated?: boolean;
  /** Lift + deepen the shadow on hover (and enable the pointer cursor). */
  interactive?: boolean;
  /** Inner padding in px. Defaults to 18 (prototype default). */
  padding?: number;
  /** Click handler — promotes the card to a focusable `role="button"`. */
  onClick?: () => void;
  /** Additional classes. */
  className?: string;
  /** Card content. */
  children: ReactNode;
}

/**
 * Renders a token-styled surface. `data-testid="g-card"`;
 * `data-interactive` / `data-elevated` aid test selection.
 */
export function Card({
  elevated = false,
  interactive = false,
  padding = 18,
  onClick,
  className,
  children,
}: CardProps): ReactElement {
  const restingShadow = elevated
    ? "shadow-[var(--g-shadow-md)]"
    : "shadow-[var(--g-shadow-sm)]";

  const interactiveClasses = interactive
    ? "transition-[transform,box-shadow] duration-200 ease-[var(--g-ease)] hover:-translate-y-0.5 hover:shadow-[var(--g-shadow-md)]"
    : "";

  const clickableClasses = onClick
    ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    : "";

  const classes = [
    "bg-card",
    "border border-border",
    "rounded-[var(--g-radius)]",
    restingShadow,
    interactiveClasses,
    clickableClasses,
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleKeyDown = onClick
    ? (event: ReactKeyboardEvent<HTMLDivElement>): void => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }
    : undefined;

  return (
    <div
      data-testid="g-card"
      data-interactive={interactive ? "true" : "false"}
      data-elevated={elevated ? "true" : "false"}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={classes}
      style={{ padding }}
    >
      {children}
    </div>
  );
}
